<template>
  <div class="dt-calendar">
    <table class="dt-calendar__table">
      <thead class="dt-calendar__control-header">
        <tr>
          <th class="dt-calendar__prev-month">
            <v-icon @click="prevMonth" v-text="`mdi-chevron-left`" />
          </th>
          <th colspan="5">
            <div class="dt-calendar__controls">
              <div>
                <select v-model="month" class="dt-calendar__monthselect">
                  <option v-for="m in months" :key="m.value" :value="m.value">{{
                    m.label
                  }}</option>
                </select>
              </div>
              <div>
                <select v-model="year" class="dt-calendar__yearselect">
                  <option v-for="y in years" :key="y" :value="y">{{
                    y
                  }}</option>
                </select>
              </div>
            </div>
          </th>
          <th class="dt-calendar__next-month">
            <v-icon @click="nextMonth" v-text="`mdi-chevron-right`" />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr class="dt-calendar__weeks">
          <th
            v-for="weekDay in locale.daysOfWeek"
            :key="weekDay"
            class="dt-calendar__cell"
          >
            {{ weekDay }}
          </th>
        </tr>
        <tr v-for="(dateRow, index) in calendar" :key="index">
          <td
            v-for="(date, idx) in dateRow"
            :key="idx"
            :class="dayClass(date)"
            class="dt-calendar__cell"
            @click="onClickDate(date)"
            @mouseover="onHoverDate(date)"
          >
            <div>
              {{ date.day }}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { DateTime, Info } from "luxon";
import { localeData } from "./util";
export default {
  name: "Calendar",
  props: {
    monthDate: DateTime,
    start: DateTime,
    end: DateTime,
    minDate: Date,
    maxDate: Date,
    localeData: {
      type: Object,
      default: () => ({})
    },
    maxRange: {
      type: [Number],
      default: 0
    },
    inSelection: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentMonthDate: this.monthDate || DateTime.local()
    };
  },
  computed: {
    monthName() {
      return this.locale.monthNames[this.currentMonthDate.getMonth()];
    },
    year: {
      get() {
        return this.currentMonthDate.year;
      },
      set(year) {
        this.changeMonthDate(DateTime.local(year, this.month));
      }
    },
    month: {
      get() {
        return this.currentMonthDate.month;
      },
      set(month) {
        this.changeMonthDate(DateTime.local(this.year, month));
      }
    },
    calendar() {
      const firstDate = this.currentMonthDate.startOf("month");
      const daysInLastMonth = firstDate.minus({ months: 1 }).daysInMonth;
      const dayOfWeek = firstDate.weekday;
      const calendar = [];

      for (let i = 0; i < 6; i++) {
        calendar[i] = [];
      }

      let startDay = daysInLastMonth - dayOfWeek + this.locale.firstDay + 1;
      if (startDay > daysInLastMonth) startDay -= 7;

      if (dayOfWeek === this.locale.firstDay) startDay = daysInLastMonth - 6;

      let curDate = firstDate.minus({ month: 1 }).set({ day: startDay });
      for (
        let i = 0, col = 0, row = 0;
        i < 6 * 7;
        i++, col++, curDate = curDate.plus({ days: 1 })
      ) {
        if (i > 0 && col % 7 === 0) {
          col = 0;
          row++;
        }
        calendar[row][col] = curDate.startOf("day");
      }
      return calendar;
    },
    months() {
      return Info.months().map((m, idx) => ({ label: m, value: idx + 1 }));
    },
    years() {
      const year = this.currentMonthDate.year;
      const years = [];
      for (let i = year + 5; i > year - 10; i--) {
        years.push(i);
      }
      return years;
    },
    locale() {
      return localeData(this.localeData, this.$i18n);
    }
  },
  watch: {
    monthDate(value) {
      this.changeMonthDate(value, false);
    }
  },
  methods: {
    prevMonth() {
      const { currentMonthDate } = this;
      this.changeMonthDate(
        currentMonthDate.minus({ months: 1 }).startOf("month")
      );
    },
    nextMonth() {
      const { currentMonthDate } = this;
      this.changeMonthDate(
        currentMonthDate.plus({ months: 1 }).startOf("month")
      );
    },
    changeMonthDate(dt, emit = true) {
      const isSameMonth = dt.hasSame(this.currentMonthDate, "month");
      this.currentMonthDate = dt;
      if (emit && !isSameMonth) {
        this.$emit("change-month", dt);
      }
    },
    dayClass(date) {
      const { start, end, month, inSelection, maxRange } = this;
      const classes = {
        "dt-calendar__cell--off": date.month !== month,
        "dt-calendar__cell--weekend": date.weekday > 5
      };
      if (date.month === month) {
        classes["dt-calendar__cell--today"] = date.hasSame(
          DateTime.local(),
          "day"
        );
        if (start && end) {
          classes["dt-calendar__cell--in-range"] =
            date > start.startOf("day") && date < end.startOf("day");
          classes["dt-calendar__cell--active"] =
            date.hasSame(start, "day") || date.hasSame(end, "day");
        }
        if (start) {
          classes["dt-calendar__cell--disabled"] =
            inSelection &&
            maxRange > 0 &&
            date > start.plus({ days: maxRange - 1 });
          classes["dt-calendar__cell--start-date"] = date.hasSame(start, "day");
        }
        if (end) {
          classes["dt-calendar__cell--end-date"] = date.hasSame(end, "day");
        }
      }

      return classes;
    },
    onClickDate(date) {
      if (date.month !== this.month) return;
      this.$emit("dateClick", date);
    },
    onHoverDate(date) {
      if (date.month !== this.month) return;
      this.$emit("hoverDate", date);
    }
  }
};
</script>
